import {Component, Input, OnChanges, SimpleChanges, OnInit, OnDestroy, ElementRef, ViewChild, NgZone, inject} from '@angular/core';
import {CommonModule} from '@angular/common';
import {Subscription, combineLatest, of, catchError, switchMap} from 'rxjs';
import {Location, BuildingType} from '@flowmaps-com/flowmaps-typescriptmodels';
import {View} from '../../../common/view';
import {Handler} from '../../../common/handler';
import {TranslateDirective} from '../../../common/utils/translate.directive';
import {RefdataUtils} from '../../refdata/refdata-utils';
import {CrremReferenceService} from '../../../utils/crrem/crrem-reference.service';
import {CrremMeasurementChannel, CrremMeasurementsResult, crremDataMatchesLocation} from '../../../utils/crrem/crrem-measurements';
import {assessCrrem, CrremAssessment, CrremMetric, CrremReference, CrremYear, firstExceedance, projectCrrem} from '../../../utils/crrem/crrem-calculation';

@Component({selector:'app-crrem-compass',standalone:true,imports:[CommonModule],
    templateUrl:'./crrem-compass.component.html',styleUrls:['./crrem-compass.component.scss']})
@Handler()
export class CrremCompassComponent extends View implements OnInit, OnChanges, OnDestroy {
    @Input() location: Location;
    @Input() display = {projection: true, crossing: true, target: true};
    readonly metric: CrremMetric = 'carbon';
    hoveredYear: number | null = null;
    tooltipLeft = 0;
    tooltipTop = 0;
    tooltipArrowLeft = 120;
    @ViewChild('chartTooltip') chartTooltip?: ElementRef<HTMLElement>;
    showTooltip(year: number, event: MouseEvent | FocusEvent) {
        if (this.hoveredYear === year) return;
        const bounds = (event.currentTarget as Element).getBoundingClientRect();
        const yearX = bounds.x + bounds.width / 2;
        const width = this.chartTooltip?.nativeElement.getBoundingClientRect().width || Math.min(240, window.innerWidth - 16);
        const left = Math.max(8, Math.min(yearX - width / 2, window.innerWidth - width - 8));
        const chart = (event.currentTarget as Element).closest('svg').getBoundingClientRect();
        this.tooltipLeft = left - chart.left;
        this.tooltipArrowLeft = yearX - left;
        this.hoveredYear = year;
    }
    showInfo = false;
    hoverYears: number[] = [];
    loading = true;
    failed = false;
    assessment: CrremAssessment;
    reference: CrremReference;
    base: CrremYear;
    projection: {year:number;value:number;target:number}[] = [];
    crossing: number | null = null;
    maxY = 1;
    yearTicks: number[] = [];
    ticks: number[] = [];
    targetPath = '';
    historyPaths: string[] = [];
    forecastPath = '';
    chartWidth = 600;
    chartHeight = 260;
    readonly plotLeft = 54;
    readonly plotTop = 40;
    get plotRight() { return this.chartWidth - 18; }
    get plotBottom() { return this.chartHeight - 26; }
    get yearBandWidth() { return (this.plotRight - this.plotLeft) / (2050 - this.startYear); }
    private resizeObserver?: ResizeObserver;
    private zone = inject(NgZone);
    @ViewChild('chartContainer') set chartContainer(container: ElementRef<HTMLElement> | undefined) {
        this.resizeObserver?.disconnect();
        if (!container) return;
        this.resizeObserver = new ResizeObserver(([entry]) => {
            const width = Math.round(entry.contentRect.width);
            const height = Math.round(entry.contentRect.height);
            if (width < 100 || height < 80 || (width === this.chartWidth && height === this.chartHeight)) return;
            this.zone.run(() => {
                this.chartWidth = width;
                this.chartHeight = height;
                this.render();
            });
        });
        this.resizeObserver.observe(container.nativeElement);
    }
    private sub: Subscription;
    private started = false;
    private refService = inject(CrremReferenceService);
    t(key:string):string { return TranslateDirective.getTranslation(key,true); }
    ngOnInit() { this.started=true; this.load(); }
    ngOnChanges(changes: SimpleChanges) { if(this.started && changes.location) this.load(); }
    ngOnDestroy() { this.resizeObserver?.disconnect(); this.sub?.unsubscribe(); this.onDestroy(); }
    private load() {
        this.sub?.unsubscribe(); this.loading=true; this.failed=false; this.assessment=null; this.base=null;
        const location=this.location;
        if(!location) {this.loading=false;return;}
        const building$=location.info?.buildingType?this.sendQuery('getBuildingType',location.info.buildingType):of(null);
        const measurements$ = this.sendQuery('getCrremMeasurementChannel').pipe(
            switchMap((channel: CrremMeasurementChannel) => channel.measurements$));
        this.sub=combineLatest([this.refService.data$,measurements$,building$])
            .pipe(catchError(()=>of(null))).subscribe(result=>{
                if(this.location!==location) return;
                if(!result) {this.loading=false;this.failed=true;return;}
                const [ref,data,building]=result as [CrremReference,CrremMeasurementsResult,BuildingType];
                if (!data.unavailable && !crremDataMatchesLocation(data, location.locationId)) {
                    this.loading=true; this.assessment=null; this.base=null; return;
                }
                this.loading=!!data?.loading; this.failed=!!data?.failed || !!data?.unavailable;
                if(this.loading || this.failed) {this.assessment=null;this.base=null;return;}
                this.reference=ref;
                const productionExpected=(location.connections || []).some(c=>(c.meters || []).some(m=>
                    (m.details?.type || m.info?.type)==='GROSS_PRODUCTION'));
                this.assessment=assessCrrem(ref,{area:RefdataUtils.getLocationArea(location),country:location.info?.address?.country,
                    buildingName:building?.info?.name,buildingCode:building?.info?.code,
                    allElectric:location.info?.allElectric===true,productionExpected,
                    measurements:data?.measurements,currentYear:new Date().getFullYear()});
                for(const year of this.assessment.years) {
                    const allCoverage=data.coverage?.[year.year] || [];
                    const relevant=allCoverage.filter(c=>c.connectionType==='Electricity' || (!location.info?.allElectric
                        && ['Gas','Heat','Cooling'].includes(c.connectionType)));
                    if(!relevant.length) year.warnings.push('crrem_coverage_unknown');
                    else if(relevant.some(c=>!c.meterCompleteness?.length || c.meterCompleteness.some(m=>m.completeness<0.999999)))
                        year.warnings.push('crrem_meter_gaps');
                    if(!productionExpected && year.coverage.production===0 && year.energy.exported===0)
                        year.warnings.push('crrem_generation_unconfirmed');
                    year.warnings=[...new Set(year.warnings)];
                }
                this.base=this.assessment.years[this.assessment.years.length-1];
                this.render();
            });
    }
    get tooltip() {
        if(this.hoveredYear==null) return null;
        const year=this.hoveredYear;
        const actual=this.assessment?.years.find(p=>p.year===year);
        const forecast=this.projection.find(p=>p.year===year);
        return {year,actual:actual?.carbon,forecast:forecast?.value,
            target:this.assessment?.pathway?.carbon[year-2020],
            warnings:actual?.warnings || [],crossing:this.crossing};
    }
    get comparisonLabel() {return this.t(this.assessment?.pathwayApproximate ? 'crrem_approximate_comparison' : 'crrem_indicative_short');}
    get referenceExplanation() {
        return this.t('crrem_approximate_description').replace('{pathway}', this.t(this.assessment?.pathway?.name || ''));
    }
    get unit() {return (this.metric==='energy'?'kWh/m²/':'kg CO₂e/m²/')+this.t('crrem_year_unit');}
    get currentValue() {return this.base?(this.metric==='energy'?this.base.eui:this.base.carbon):null;}
    get targetValue() {return this.base&&this.assessment?.pathway?this.assessment.pathway[this.metric][this.base.year-2020]:null;}
    private readonly assumptionKeys = new Set(['crrem_gas_conversion', 'crrem_generation_unconfirmed', 'crrem_thermal_factor_estimated']);
    private get assessmentNotes() {return [...new Set([...(this.assessment?.warnings || []),...(this.base?.warnings || [])])];}
    get issues() {return this.assessmentNotes.filter(key => !this.assumptionKeys.has(key));}
    get assumptions() {return this.assessmentNotes.filter(key => this.assumptionKeys.has(key));}
    get basisText() {
        const country = this.t(this.assessment?.region?.country || this.location?.info?.address?.country || '');
        return [this.t('crrem_boundary'),
            this.t(this.location?.info?.allElectric ? 'crrem_all_electric' : 'crrem_thermal_rule'),
            this.t('crrem_assumptions').replace('{country}', country),
            ...this.assumptions.map(key => this.t(key)),
            this.base?.coverage.heat > 0 ? this.t('crrem_heat_conversion') : '']
            .filter(Boolean).join(' ');
    }
    get provisional() {return this.issues.length>0;}
    get historicPoints() {return (this.assessment?.years || []).filter(y=>this.metric==='energy'||y.carbon!=null);}
    pointValue(p:CrremYear) {return this.metric==='energy'?p.eui:p.carbon;}
    x(year:number) {return this.plotLeft+(year-this.startYear)/(2050-this.startYear)*(this.plotRight-this.plotLeft);}
    y(value:number) {return this.plotBottom-Math.max(0,value)/this.maxY*(this.plotBottom-this.plotTop);}
    get startYear() {return Math.min(this.assessment?.years[0]?.year || new Date().getFullYear()-1,2049);}
    private path(points:{year:number;value:number}[]) {return points.map((p,i)=>`${i?'L':'M'}${this.x(p.year).toFixed(2)},${this.y(p.value).toFixed(2)}`).join(' ');}
    private render() {
        if(!this.assessment) return;
        this.hoveredYear=null;
        this.hoverYears=Array.from({length:2051-this.startYear},(_,i)=>this.startYear+i);
        const target=this.assessment.pathway;
        this.projection=this.base?projectCrrem(this.reference,this.assessment,this.base,this.metric):[];
        this.crossing=firstExceedance(this.projection);
        const targetPoints=target?Array.from({length:2051-this.startYear},(_,i)=>({year:this.startYear+i,value:target[this.metric][this.startYear+i-2020]})):[];
        this.maxY=Math.max(1,...targetPoints.map(p=>p.value),...this.projection.map(p=>p.value),...this.historicPoints.map(p=>this.pointValue(p)))*1.13;
        this.ticks=Array.from({length:5},(_,i)=>this.maxY*i/4);
        this.yearTicks=[this.startYear];
        for (const year of [2025,2030,2035,2040,2045]) {
            if (year > this.startYear && this.x(year)-this.x(this.yearTicks[this.yearTicks.length-1]) >= 40
                && this.x(2050)-this.x(year) >= 40) this.yearTicks.push(year);
        }
        this.yearTicks.push(2050);
        this.targetPath=this.path(targetPoints); this.forecastPath=this.path(this.projection);
        const groups:CrremYear[][]=[];
        for(const p of this.historicPoints) {
            const last=groups[groups.length-1];
            if(last&&last[last.length-1].year===p.year-1) last.push(p); else groups.push([p]);
        }
        this.historyPaths=groups.map(g=>this.path(g.map(p=>({year:p.year,value:this.pointValue(p)}))));
    }
}
